/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 1:24:18 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useListEditorContext } from "lib/providers";
import { ActivationCondition } from "lib/schemas";
import { ConditionStatement } from "common/components/conditions";
import { useDeleteActivationCondition } from "service-builder/services/OutgoingBranchesConfigurationAPI";
import { DeleteOSItemDialog } from "version-control/components/DeleteOSItemDialog";
import { ActivationConditionForm } from "./ActivationConditionForm";

interface ActivationConditionListItemProps {
  activationCondition: ActivationCondition;
}

export const ActivationConditionListItem = ({ activationCondition }: ActivationConditionListItemProps) => {
  const {
    updateList,
    selectItem,
    removeItem,
    itemsListState: { selectedItemId },
    dequeueNewItem,
  } = useListEditorContext<ActivationCondition>();

  const showForm = selectedItemId === activationCondition.id || !activationCondition?.id;

  const onCloseForm = () => {
    selectItem(null);
    if (!activationCondition.id) dequeueNewItem((activationCondition as any).os_unsaved_item_temp_unique_id);
  };

  const onSuccessfulAPISaveCondition = (savedCondition: ActivationCondition) => {
    updateList(savedCondition, { newItemToRemoveTempId: (activationCondition as any).os_unsaved_item_temp_unique_id });
  };

  const onSelectCondition = () => selectItem(activationCondition.id);

  const onDeleteCondition = () => removeItem(activationCondition.id);

  return showForm ? (
    <ActivationConditionForm
      activationCondition={activationCondition}
      updateActivationConditionFromAPICallback={onSuccessfulAPISaveCondition}
      onClose={onCloseForm}
    />
  ) : (
    <Paper elevation={10}>
      <ConditionStatement
        condition={activationCondition}
        actions={
          <>
            <Tooltip title={<Typography>Edit Condition</Typography>}>
              <IconButton onClick={onSelectCondition} color="primary">
                <EditIcon />
              </IconButton>
            </Tooltip>
            <DeleteOSItemDialog
              itemId={activationCondition.id}
              onDelete={onDeleteCondition}
              onRestore={() => {}}
              useDeleteOSItem={useDeleteActivationCondition}
            >
              {({ handleOpen }) => (
                <Tooltip title={<Typography>Delete Condition</Typography>}>
                  <IconButton onClick={handleOpen} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </DeleteOSItemDialog>
          </>
        }
      />
    </Paper>
  );
};
